import { TrashIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, memo, ReactNode, TextareaHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

import { secondaryTextArea } from '@/styles/styles';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { errorMessage } from '@/utils/animations/commom';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import ConditionalRenderer from '../ConditionalRenderer';
import IconButton from '../buttons/IconButton';
import Label, { labelCssModifier, LabelProps } from './Label';

type OmitTextAreaDefaultClassname = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
>;

type Props = OmitTextAreaDefaultClassname & {
  label?: LabelProps;
  className?: ClassNameComponent;
  error?: string | boolean;
  onClear?: () => void;
  testId?: string;
  clearWrapper?: ReactNode;
  rows?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
};

const SecondaryTextArea = forwardRef<HTMLTextAreaElement, Props>(
  (props: Props, ref) => {
    const {
      id,
      label,
      className,
      error,
      disabled,
      onClear,
      testId,
      clearWrapper,
      rows = 3,
      resize = 'vertical',
      ...rest
    } = props;

    return (
      <div
        className={twMerge(
          'relative flex flex-col',
          labelCssModifier(label),
          className?.div,
        )}
      >
        <div
          className={twMerge(
            secondaryTextArea,
            'grow flex gap-1 min-h-20',
            'transition-all duration-200 ease-in-out',
            error && !disabled && 'border-error focus-within:border-error',
            disabled && 'pointer-events-none disabled',
            className?.base,
          )}
        >
          <div className="flex items-start gap-1 pt-1">
            <ConditionalRenderer condition={label}>
              {label && <Label label={label} htmlFor={id} />}
            </ConditionalRenderer>
            <AnimatePresence>
              {error && !disabled && (
                <motion.label
                  className="text-error text-[0.875rem] overflow-hidden max-h-5"
                  {...errorMessage}
                >
                  *
                </motion.label>
              )}
            </AnimatePresence>
          </div>
          <textarea
            ref={ref}
            {...rest}
            id={id}
            rows={rows}
            data-testid={testId}
            disabled={disabled}
            className={twMerge(
              'grow focus:outline-hidden flex w-full bg-transparent px-2  min-h-20',
              `resize-${resize}`,
              scrollBarYClassName,
              className?.input,
            )}
          />
          <ConditionalRenderer condition={onClear}>
            <div className="flex items-start pt-1">
              <IconButton
                type="button"
                className={className?.button}
                icon={
                  <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                }
                onClick={onClear}
              />
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={clearWrapper}>
            {clearWrapper}
          </ConditionalRenderer>
        </div>
      </div>
    );
  },
);

export default memo(SecondaryTextArea);
